import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import { Badge } from "components/ChecklistRow";
import ManualDoPortalButton from "components/ManualDoPortalButton";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { ChavesModal, GerarNovaSenha, MostrarSenha } from "components/AcessoSftp";
import { useToast } from "hooks/toast";
import { acessoSftpMeuFreteApi, listarAcessoSftp, mensagemErro } from "services/meuFreteApi";

// MeuFrete › Integrações › Acesso SFTP (PO1-2309, épico PO1-3279). Um card por
// embarcador atendido, com o acesso SFTP do vínculo: host, porta, usuário,
// pastas, modo e as ações sobre o segredo (Mostrar senha, Gerar nova senha,
// Chaves SSH). Nunca cria acesso: quem cria é o Onboarding de Transportadora da
// Everlog. Reusa os componentes de components/AcessoSftp com as rotas
// /meufrete/acesso-sftp/... (`acessoSftpMeuFreteApi`).
//
//   gerenciado     ações completas;
//   legado         usuário criado na console: "Gerar nova senha" adota o acesso;
//   em_onboarding  a Everlog ainda está montando: sem ações;
//   pendente       operação em andamento no Wing: aguarde;
//   revogado       acesso revogado: sem ações.

const OCULTAR_SENHA_MS = 30 * 1000;

const ESTADOS = {
  gerenciado: { rotulo: "Ativo", badge: "done" },
  legado: { rotulo: "Acesso antigo", badge: "pend" },
  em_onboarding: { rotulo: "Em onboarding pela Everlog", badge: "auto" },
  pendente: { rotulo: "Operação em andamento", badge: "auto" },
  revogado: { rotulo: "Acesso revogado", badge: "lock" },
};

const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };
const rowStyle = { display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.35rem 0", fontSize: "0.86rem" };
const keyStyle = { color: AUTOPILOT_COLORS.muted };
const valStyle = { color: AUTOPILOT_COLORS.text, textAlign: "right", wordBreak: "break-all" };
const avisoStyle = (tone) => ({
  background: tone === "warning" ? AUTOPILOT_COLORS.statusWarningBg : tone === "danger" ? AUTOPILOT_COLORS.statusDangerBg : AUTOPILOT_COLORS.insetSurface,
  border: `1px solid ${tone === "warning" ? AUTOPILOT_COLORS.statusWarningBorder : tone === "danger" ? AUTOPILOT_COLORS.statusDangerBorder : AUTOPILOT_COLORS.border}`,
  color: tone === "warning" ? AUTOPILOT_COLORS.statusWarning : tone === "danger" ? AUTOPILOT_COLORS.statusDanger : AUTOPILOT_COLORS.text,
  borderRadius: 8,
  padding: "0.6rem 0.8rem",
  fontSize: "0.82rem",
  marginBottom: "0.6rem",
});
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: "1rem" };

function Linha({ k, v }) {
  return (
    <div style={rowStyle}>
      <span style={keyStyle}>{k}</span>
      <span style={valStyle}>{v}</span>
    </div>
  );
}

function formatarData(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString("pt-BR");
}

function CardEmbarcador({ item, host, porta, onChanged }) {
  const [chavesAberto, setChavesAberto] = useState(false);
  // A senha recém-gerada aparece uma vez aqui (vem na resposta do POST) e some em 30 s.
  const [senhaNova, setSenhaNova] = useState(null);
  useEffect(() => {
    if (!senhaNova) return undefined;
    const t = setTimeout(() => setSenhaNova(null), OCULTAR_SENHA_MS);
    return () => clearTimeout(t);
  }, [senhaNova]);

  const idEmbarcador = item.embarcador && item.embarcador.id;
  const estado = ESTADOS[item.estado] || { rotulo: item.estado || "—", badge: "lock" };
  const gerenciado = item.estado === "gerenciado";
  const legado = item.estado === "legado";
  const modoChave = item.modo === "chave" || item.modo === "ssh";
  const semAcoes = item.estado === "em_onboarding" || item.estado === "revogado" || item.estado === "pendente";
  const podeMostrarSenha = gerenciado && !modoChave && !!item.temSenha;
  const podeGerarSenha = (gerenciado && !modoChave) || legado;
  const podeChaves = gerenciado && modoChave;

  return (
    <ShellCard
      eyebrow="Embarcador"
      title={(item.embarcador && item.embarcador.nome) || `Embarcador ${idEmbarcador}`}
      action={<Badge tipo={estado.badge}>{estado.rotulo}</Badge>}
      style={{ marginTop: 0 }}
    >
      {item.estado === "em_onboarding" ? (
        <div style={avisoStyle()}>A Everlog ainda está montando o acesso deste embarcador (pastas e usuário SFTP). Quando terminar, as credenciais aparecem aqui.</div>
      ) : null}
      {item.estado === "revogado" ? <div style={avisoStyle("danger")}>Acesso revogado. Se precisar reativar, fale com o embarcador.</div> : null}
      {item.estado === "pendente" ? <div style={avisoStyle("warning")}>Operação em andamento no servidor. Atualize em instantes.</div> : null}
      {legado ? (
        <div style={avisoStyle("warning")}>Acesso antigo: gere uma nova senha para gerenciar por aqui. O usuário e as pastas continuam os mesmos; só a senha muda.</div>
      ) : null}

      {/* Conexão, Credencial e Pastas empilhadas: o card tem ~420-480 px (grid minmax 420px)
          e, lado a lado, a caixa da senha ficava com ~150 px e quebrava uma letra por linha. */}
      <div className="row">
        <div className="col-12">
          <SectionCard title="Conexão">
            <Linha k="Host" v={<code style={{ color: AUTOPILOT_COLORS.text }}>{host || "—"}</code>} />
            <Linha k="Porta" v={porta || "—"} />
            <Linha k="Usuário" v={item.usuario ? <code style={{ color: AUTOPILOT_COLORS.text }}>{item.usuario}</code> : "—"} />
            <Linha k="Autenticação" v={item.estado === "em_onboarding" ? "—" : modoChave ? `Chave SSH (${item.chaves || 0} de 3)` : legado && !item.modo ? "Senha (definida na console)" : "Usuário e senha"} />
            {item.dataSenha ? <Linha k="Senha desde" v={formatarData(item.dataSenha)} /> : null}
            <Linha k="Último acesso" v={item.ultimoAcessoEm ? formatarData(item.ultimoAcessoEm) : "—"} />
          </SectionCard>
        </div>
        <div className="col-12">
          <SectionCard title="Credencial" style={{ marginTop: "0.75rem" }}>
            {semAcoes ? <div style={hintStyle}>Sem ações disponíveis neste estado.</div> : null}

            {senhaNova ? (
              <div style={avisoStyle("warning")}>
                <div style={{ fontSize: "0.78rem", marginBottom: "0.3rem" }}>Senha nova (some em 30 s; depois use "Mostrar senha"):</div>
                <code style={{ fontSize: "0.95rem", color: AUTOPILOT_COLORS.title, wordBreak: "break-all" }}>{senhaNova}</code>
              </div>
            ) : null}

            {podeMostrarSenha ? (
              <MostrarSenha idEmbarcador={idEmbarcador} idTransportadora={item.idTransportadora} api={acessoSftpMeuFreteApi} ocultarMs={OCULTAR_SENHA_MS} />
            ) : null}
            {gerenciado && !modoChave && !item.temSenha ? (
              <div style={{ ...hintStyle, marginBottom: "0.6rem" }}>Nenhuma senha guardada. Gere uma nova para começar a usar.</div>
            ) : null}
            {podeChaves ? (
              <div style={{ ...hintStyle, marginBottom: "0.6rem" }}>Acesso por chave pública. Não há senha guardada.</div>
            ) : null}

            {podeGerarSenha ? (
              <GerarNovaSenha
                idEmbarcador={idEmbarcador}
                idTransportadora={item.idTransportadora}
                legado={legado}
                api={acessoSftpMeuFreteApi}
                semEmail
                onGerada={(r) => setSenhaNova((r && r.senha) || null)}
                onChanged={onChanged}
              />
            ) : null}
            {podeChaves ? (
              <div style={{ marginTop: "0.6rem" }}>
                <Button color="info" outline size="sm" onClick={() => setChavesAberto(true)}>
                  Gerenciar chaves ({item.chaves || 0}/3)
                </Button>
              </div>
            ) : null}
            {!semAcoes ? (
              <p style={{ ...hintStyle, margin: "0.6rem 0 0" }}>Cada exibição de senha fica registrada (quem, quando). A senha nunca vai por e-mail.</p>
            ) : null}
          </SectionCard>
          <SectionCard title="Pastas" style={{ marginTop: "0.75rem" }}>
            {(item.pastas || []).map((p) => (
              <div key={p} style={{ ...rowStyle, justifyContent: "flex-start" }}>
                <code style={{ color: AUTOPILOT_COLORS.text }}>{p}</code>
                <span style={hintStyle}>{p.indexOf("/CT/") === 0 ? "CT-e (XML)" : p.indexOf("/FT/") === 0 ? "Fatura" : p.indexOf("/OC/") === 0 ? "Ocorrências" : ""}</span>
              </div>
            ))}
            <p style={{ ...hintStyle, margin: "0.4rem 0 0" }}>Envie os arquivos na pasta Origem de cada tipo; o retorno fica nas pastas irmãs.</p>
          </SectionCard>
        </div>
      </div>

      {chavesAberto ? (
        <ChavesModal
          idEmbarcador={idEmbarcador}
          idTransportadora={item.idTransportadora}
          chaves={item.listaChaves || []}
          api={acessoSftpMeuFreteApi}
          onClose={() => setChavesAberto(false)}
          onChanged={onChanged}
        />
      ) : null}
    </ShellCard>
  );
}

export default function AcessoSftp() {
  const { addToast } = useToast();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const d = await listarAcessoSftp();
      setDados(d);
    } catch (e) {
      const msg = mensagemErro(e, "Não foi possível carregar os acessos.");
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "Acesso SFTP", description: msg });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const itens = (dados && dados.itens) || [];

  return (
    <div className="content">
      <PageHero
        eyebrow="Integrações"
        title="Acesso SFTP"
        description="A credencial SFTP de cada embarcador que você atende: usuário, pastas e a senha ou as chaves. O acesso é criado pela Everlog no onboarding; aqui você gerencia o segredo."
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <ManualDoPortalButton />
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
          </div>
        }
      />

      {loading && !dados ? (
        <ShellCard>
          <div style={stateBoxStyle}>
            <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
            <span>Carregando os acessos…</span>
          </div>
        </ShellCard>
      ) : erro ? (
        <ShellCard>
          <div style={stateBoxStyle} role="alert">
            {erro}{" "}
            <Button size="sm" color="info" outline onClick={carregar}>
              Tentar novamente
            </Button>
          </div>
        </ShellCard>
      ) : dados && dados.configurado === false ? (
        <ShellCard>
          <div style={stateBoxStyle}>O gerenciamento de acesso SFTP não está disponível neste ambiente. Fale com a Everlog.</div>
        </ShellCard>
      ) : itens.length === 0 ? (
        <ShellCard>
          <div style={stateBoxStyle}>Nenhum embarcador com vínculo ativo. Quando um embarcador te adicionar, o acesso dele aparece aqui.</div>
        </ShellCard>
      ) : (
        <div style={gridStyle}>
          {itens.map((item) => (
            <CardEmbarcador key={`${item.embarcador && item.embarcador.id}-${item.idTransportadora}`} item={item} host={dados.host} porta={dados.porta} onChanged={carregar} />
          ))}
        </div>
      )}
    </div>
  );
}
